import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Home, ArrowRight, User, Phone, Mail, KeyRound, Building } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Signup() {
  usePageTitle("Create Account");
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/portal";

  const [form, setForm] = useState({ name: "", phone: "", email: "", company_name: "", password: "" });
  const [waOptin, setWaOptin] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup`, { ...form, whatsapp_optin: waOptin });
      // Persist token
      localStorage.setItem("ba_token", data.token);
      localStorage.setItem("ba_user", JSON.stringify(data.user));
      // Merge guest cart if any
      const sid = localStorage.getItem("ba_shop_sid");
      if (sid) {
        try {
          await axios.post(`${API}/shop/cart/merge`, { session_id: sid }, {
            headers: { Authorization: `Bearer ${data.token}` },
          });
          localStorage.removeItem("ba_shop_sid");
        } catch (_) {}
      }
      toast.success("Account created — welcome!");
      try { await login(form.phone, form.password); } catch (_) {}
      nav(next);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sign up failed";
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-between p-10 lg:p-16">
        <div className="flex items-center justify-between">
          <Logo height={56} variant="light" />
          <div className="flex gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 border-2 border-zinc-200 rounded-full hover:border-[#FF6B00] hover:text-[#FF6B00]" data-testid="signup-home-btn"><Home size={12} /> Home</Link>
            <Link to={`/login${next !== "/portal" ? `?next=${encodeURIComponent(next)}` : ""}`} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 bg-zinc-100 rounded-full hover:bg-zinc-200" data-testid="signup-login-btn">Log In</Link>
          </div>
        </div>

        <div className="max-w-md">
          <div className="overline text-[#FF6B00] mb-4">Create Account</div>
          <h1 className="font-display text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter mb-4">
            Join
            <br />
            <span className="text-[#FF6B00]">1,000+</span> businesses.
          </h1>
          <p className="text-zinc-600 mb-6">Track orders, reorder in one click, upload artwork anytime. Signup is free.</p>

          <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
            <Row icon={User} label="Full Name*">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} required className="input" data-testid="signup-name" />
            </Row>
            <Row icon={Phone} label="Mobile Number*">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} required className="input" placeholder="+91 8070 970 980" data-testid="signup-phone" />
            </Row>
            <Row icon={Mail} label="Email (optional but recommended)">
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input" data-testid="signup-email" />
            </Row>
            <Row icon={Building} label="Company / Business (optional)">
              <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="input" data-testid="signup-company" />
            </Row>
            <Row icon={KeyRound} label="Password* (min 6 chars)">
              <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="input" data-testid="signup-password" />
            </Row>
            <label className="flex items-start gap-2.5 text-sm text-zinc-700 cursor-pointer" data-testid="signup-wa-optin-wrap">
              <input type="checkbox" checked={waOptin} onChange={(e) => setWaOptin(e.target.checked)} className="mt-1 accent-[#FF6B00]" data-testid="signup-wa-optin" />
              <span>Get order updates, payment receipts &amp; offers on WhatsApp on the number above. <span className="text-zinc-400">(Recommended — you can opt out anytime)</span></span>
            </label>
            {err && <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="signup-error">{err}</div>}
            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#e65a00] text-white font-bold px-6 py-3 rounded-full uppercase tracking-widest text-sm disabled:opacity-60" data-testid="signup-submit">
              {busy ? "Creating account…" : "Create Account"} <ArrowRight size={14} />
            </button>
          </form>

          {/* Google sign-in — customer portal only */}
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-zinc-200" />
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">or</div>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/portal";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 border-2 border-zinc-200 hover:border-[#FF6B00] bg-white text-black px-4 py-2.5 font-semibold rounded-full transition-colors"
              data-testid="signup-google-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C41.3 35.8 44 30.4 44 24c0-1.3-.1-2.6-.4-3.5z"/>
              </svg>
              Sign up with Google
            </button>
            <p className="mt-3 text-xs text-center text-zinc-500">
              Fastest way in — we'll create your portal automatically.
            </p>
          </div>

          <div className="mt-5 text-sm text-zinc-500">
            Already have an account? <Link to={`/login${next !== "/portal" ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-[#FF6B00] font-bold hover:underline" data-testid="signup-to-login">Log in</Link>
          </div>
        </div>

        <div className="text-xs text-zinc-400">© Brothers Advertising · print@brothersdigital.in</div>
      </div>

      {/* Right marketing panel */}
      <div className="hidden lg:block relative bg-zinc-900 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1600&q=80')", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.35 }} />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-[#FF6B00]/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-16 text-white">
          <div className="overline text-[#FF6B00] mb-2">Why Sign Up?</div>
          <ul className="space-y-3 text-lg">
            <li>· Track every order end-to-end</li>
            <li>· 1-click re-order for repeat prints</li>
            <li>· Upload artwork anytime, safely stored 30 days</li>
            <li>· Exclusive coupon codes & festive offers</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1 inline-flex items-center gap-1"><Icon size={12} /> {label}</label>
      {children}
    </div>
  );
}
